import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import Layout from './components/Layout';
import SalesInvoice from './pages/SalesInvoice';
import Customers from './pages/Customers';
import Items from './pages/Items';
import Reports from './pages/Reports';
import ReportForm from './pages/ReportForm';
import SalesSummaryReport from './pages/SalesSummaryReport';
import PaymentCollectionReport from './pages/PaymentCollectionReport';
import CustomerSalesReport from './pages/CustomerSalesReport';
import DSRReport from './pages/DSRReport';
import QuotationReport from './pages/QuotationReport';
import VATPurchaseReport from './pages/VATPurchaseReport';
import VATSalesReport from './pages/VATSalesReport';
import VATReturnReport from './pages/VATReturnReport';
import OtherMasters from './pages/OtherMasters';
import Shortcuts from './pages/Shortcuts';
import Login from './pages/Login';
import Register from './pages/Register';
import NewCustomer from './pages/NewCustomer';
import Suppliers from './pages/Suppliers';
import NewSupplier from './pages/NewSupplier';
import NewItem from './pages/NewItem';
import Sales from './pages/Sales';
import Quotation from './pages/Quotation';
import QuotationForm from './pages/QuotationForm';
import Purchases from './pages/Purchases';
import PurchaseInvoice from './pages/PurchaseInvoice';
import PurchaseReturn from './pages/PurchaseReturn';
import PurchaseReturnForm from './pages/PurchaseReturnForm';
import Documents from './pages/Documents';
import Subscription from './pages/Subscription';
import VATReports from './pages/VATReports';
import Dashboard from './pages/Dashboard';
import CreditNote from './pages/CreditNote';
import DebitNote from './pages/DebitNote';
import NewCreditNote from './pages/NewCreditNote';
import NewDebitNote from './pages/NewDebitNote';
import CompleteRegistration from './pages/CompleteRegistration';
import TeamMembers from './pages/TeamMembers';
import AcceptInvitation from './pages/AcceptInvitation';
import { AuthProvider, useAuth } from './context/AuthContext';
import Agents from './pages/Agents';
import ZatcaOnboarding from './pages/ZatcaOnboarding';
import Settings from './pages/Settings';
import ZatcaDetails from './pages/ZatcaDetails';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route component
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      <Route path="/accept-invitation/:token" element={
        <PublicRoute>
          <AcceptInvitation />
        </PublicRoute>
      } />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="shortcuts" element={<Shortcuts />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/new" element={<NewCustomer />} />
        <Route path="customers/edit/:id" element={<NewCustomer />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="suppliers/new" element={<NewSupplier />} />
        <Route path="suppliers/edit/:id" element={<NewSupplier />} />
        <Route path="items" element={<Items />} />
        <Route path="items/new" element={<NewItem />} />
        <Route path="items/edit/:id" element={<NewItem />} />
        <Route path="sales/new-invoice" element={<SalesInvoice />} />
        <Route path="sales/edit-invoice/:id" element={<SalesInvoice />} />
        <Route path="sales" element={<Sales />} />
        <Route path="quotation" element={<Quotation />} />
        <Route path="quotation/new" element={<QuotationForm />} />
        <Route path="quotation/edit/:quotationNo" element={<QuotationForm />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="purchases/new-invoice" element={<PurchaseInvoice />} />
        <Route path="purchase-invoice" element={<PurchaseInvoice />} />
        <Route path="purchase-invoice/:id" element={<PurchaseInvoice />} />
        <Route path="purchase-return" element={<PurchaseReturn />} />
        <Route path="purchase-returns/view/:invoiceNo" element={<PurchaseReturn />} />
        <Route path="purchase-return/new" element={<PurchaseReturnForm />} />
        <Route path="purchase-return/:id" element={<PurchaseReturnForm />} />
        <Route path="credit-notes" element={<CreditNote />} />
        <Route path="credit-notes/new" element={<NewCreditNote />} />
        <Route path="debit-notes" element={<DebitNote />} />
        <Route path="debit-notes/new" element={<NewDebitNote />} />
        <Route path="reports" element={<Reports />} />
        <Route path="reports/salesSummary" element={<SalesSummaryReport />} />
        <Route path="reports/paymentCollection" element={<PaymentCollectionReport />} />
        <Route path="reports/customerSales" element={<CustomerSalesReport />} />
        <Route path="reports/dsr" element={<DSRReport />} />
        <Route path="reports/quotationReport" element={<QuotationReport />} />
        <Route path="reports/vatPurchase" element={<VATPurchaseReport />} />
        <Route path="reports/vatSales" element={<VATSalesReport />} />
        <Route path="reports/vatReturn" element={<VATReturnReport />} />
        <Route path="reports/:reportType" element={<ReportForm />} />
        <Route path="documents" element={<Documents />} />
        <Route path="other-masters" element={<OtherMasters />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="vat-reports" element={<VATReports />} />
        <Route path="complete-registration" element={<CompleteRegistration />} />
        <Route path="team-members" element={<TeamMembers />} />
        <Route path="agents" element={<Agents />} />
        <Route path="zatca-onboarding" element={<ZatcaOnboarding />} />
        <Route path="zatca-details" element={<ZatcaDetails />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export { AppRoutes };
export default App;